/* istanbul ignore file */

import { Directive, ElementRef, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, fromEvent } from 'rxjs';

import { AnalyticsService } from './analytics.service';

@UntilDestroy()
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'ion-content',
  standalone: true,
})
export class IonContentScrollDirective {
  element = inject<ElementRef<HTMLIonContentElement>>(ElementRef);
  analytics = inject(AnalyticsService);

  constructor() {
    this.init();
  }

  async init() {
    const ionContent = this.element.nativeElement;
    const scroll = await this.element.nativeElement.getScrollElement();

    // Track scroll metrics without expensive DOM operations
    let lastScrollTop = 0;
    let scrollDistance = 0;
    let maxScrollReached = 0;

    fromEvent(scroll, 'scroll')
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe(() => {
        const currentScrollTop = scroll.scrollTop;

        // Calculate scroll metrics
        scrollDistance += Math.abs(currentScrollTop - lastScrollTop);
        maxScrollReached = Math.max(maxScrollReached, currentScrollTop);
        lastScrollTop = currentScrollTop;

        // Send optimized scroll analytics using the helper method
        this.trackScrollMetrics(scroll, currentScrollTop, scrollDistance, maxScrollReached);
      });
  }

  private trackScrollMetrics(scroll: HTMLElement, scrollTop: number, distance: number, maxScroll: number) {
    const scrollHeight = scroll.scrollHeight;
    const clientHeight = scroll.clientHeight;

    // Calculate scroll percentage
    const maxScrollPossible = scrollHeight - clientHeight;
    const scrollPercentage = maxScrollPossible > 0 ? (scrollTop / maxScrollPossible) * 100 : 0;

    // Calculate vertical scroll proportion
    const verticalScrollProportion = maxScrollPossible > 0 ? scrollTop / maxScrollPossible : 0;

    // Calculate onscreen content proportion
    const onscreenContentProportion = scrollHeight > 0 ? clientHeight / scrollHeight : 0;

    // Get page identifier for context
    const pageUrl = window.location.pathname;

    // Send optimized scroll analytics with structured data
    this.analytics.scroll(scrollTop, pageUrl, {
      scrollPercentage: Math.round(scrollPercentage * 100) / 100,
      verticalScrollProportion: Math.round(verticalScrollProportion * 100) / 100,
      onscreenContentProportion: Math.round(onscreenContentProportion * 100) / 100,
      scrollDistance: Math.round(distance),
      maxScrollReached: Math.round(maxScroll),
    });
  }
}
